import React from 'react';
import { ApiError } from '@daater/api';
import { Button } from '@daater/ui';
import { useNavigate } from '@tanstack/react-router';
import { useConfirmPaymentFromPayuMutation, useCreatePaymentOrderMutation } from '../api/queries';
import { logoutRequest } from '../api/endpoints';
import { useAccountStore } from '../lib/account-store';
import { useAuthStore } from '../lib/auth-store';
import type { PaymentOrder } from '../api/types';
import {
  buildPayuConfirmationPayload,
  formatPaymentAmount,
  getPaymentHeader,
} from './payment.utils';

const AUTO_REDIRECT_SECONDS = 6;

const getDisplayAmount = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().replace(/^"+|"+$/g, '');
    const parsed = Number(normalizedValue);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

export default function PaymentScreen() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore(state => state.clearAuth);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const account = useAccountStore(state => state.account);
  const createPaymentOrder = useCreatePaymentOrderMutation();
  const confirmPayment = useConfirmPaymentFromPayuMutation();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [confirmMessage, setConfirmMessage] = React.useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [transactionState, setTransactionState] = React.useState<number | null>(null);
  const [redirectCountdown, setRedirectCountdown] = React.useState(AUTO_REDIRECT_SECONDS);
  const [order, setOrder] = React.useState<PaymentOrder | null>(null);
  const payuFormRef = React.useRef<HTMLFormElement | null>(null);
  const hasSentConfirmation = React.useRef(false);

  const currentSearch = window.location.search;

  const queryConfirmation = React.useMemo(
    () => buildPayuConfirmationPayload(currentSearch),
    [currentSearch]
  );

  const amount = React.useMemo(() => {
    const queryAmount = new URLSearchParams(currentSearch).get('amount');
    return getDisplayAmount(account?.amount) ?? getDisplayAmount(queryAmount);
  }, [account?.amount, currentSearch]);

  const currency = account?.currency;

  const planName = React.useMemo(() => {
    if (typeof account?.plan?.name === 'string' && account.plan.name.trim()) {
      return account.plan.name;
    }
    if (typeof account?.teamData?.plan?.name === 'string' && account.teamData.plan.name.trim()) {
      return account.teamData.plan.name;
    }
    return '-';
  }, [account?.plan?.name, account?.teamData?.plan?.name]);

  React.useEffect(() => {
    if (!queryConfirmation) {
      setTransactionState(null);
      return;
    }

    setTransactionState(queryConfirmation.transactionState);
    if (hasSentConfirmation.current) return;
    hasSentConfirmation.current = true;

    const sendConfirmation = async () => {
      try {
        await confirmPayment.mutateAsync(queryConfirmation.payload);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          setSessionExpired(true);
          return;
        }
        setConfirmMessage('No se pudo confirmar el estado del pago. Por favor contacta soporte.');
      }
    };

    void sendConfirmation();
  }, [confirmPayment, queryConfirmation]);

  React.useEffect(() => {
    if (!order || !payuFormRef.current) return;
    payuFormRef.current.submit();
  }, [order]);

  const isApproved = transactionState === 4 && !sessionExpired;

  React.useEffect(() => {
    if (!isApproved) {
      setRedirectCountdown(AUTO_REDIRECT_SECONDS);
      return;
    }

    setRedirectCountdown(AUTO_REDIRECT_SECONDS);

    const timeoutId = window.setTimeout(() => {
      void navigate({ to: '/' });
    }, AUTO_REDIRECT_SECONDS * 1000);

    const intervalId = window.setInterval(() => {
      setRedirectCountdown(prev => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [isApproved, navigate]);

  const handleGoToApp = React.useCallback(() => {
    void navigate({ to: '/' });
  }, [navigate]);

  const handleLogout = React.useCallback(() => {
    void logoutRequest().then(() => {
      clearAuth();
      clearAccount();
      void navigate({ to: '/login' });
    });
  }, [clearAccount, clearAuth, navigate]);

  const handleStartPayment = async () => {
    setErrorMessage(null);
    setConfirmMessage(null);
    setSessionExpired(false);

    try {
      const newOrder = await createPaymentOrder.mutateAsync();
      if (!newOrder?.url) {
        setErrorMessage('La orden de pago está incompleta. Contacta soporte para continuar.');
        return;
      }
      setOrder(newOrder);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSessionExpired(true);
        return;
      }
      setErrorMessage('No pudimos crear la orden de pago. Inténtalo nuevamente.');
    }
  };

  const paymentHeader = getPaymentHeader(transactionState);
  const hasTransactionResult = transactionState !== null;
  const isPending = createPaymentOrder.isPending || confirmPayment.isPending;

  return (
    <section className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <div className="rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-charcoal-grey">{paymentHeader.title}</h1>
        <p className="mt-2 text-sm text-slate">{paymentHeader.description}</p>

        {confirmMessage ? (
          <p className="mt-4 rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
            {confirmMessage}
          </p>
        ) : null}

        {sessionExpired ? (
          <div className="mt-4 rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-3 text-sm text-darkish-pink">
            <p>Tu sesión expiró. Ingresa nuevamente para terminar el proceso de pago.</p>
            <Button className="mt-3" onClick={handleLogout}>
              Ir a login
            </Button>
          </div>
        ) : null}

        {!hasTransactionResult ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg border border-pale-grey-three bg-pale-grey p-4">
              <p className="text-sm text-slate">Plan: {planName}</p>
              <p className="mt-1 text-lg font-semibold text-charcoal-grey">
                Valor: ${formatPaymentAmount(amount)}
              </p>
              {currency && currency !== 'COP' ? (
                <p className="mt-1 text-xs text-slate">Se te cobrará en {currency}.</p>
              ) : null}
            </div>

            {errorMessage ? (
              <p className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
                {errorMessage}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleLogout} disabled={isPending}>
                Cerrar sesión
              </Button>
              <Button
                onClick={() => void handleStartPayment()}
                disabled={isPending || sessionExpired}
              >
                {createPaymentOrder.isPending ? 'Procesando...' : 'Pagar'}
              </Button>
            </div>
          </div>
        ) : null}

        {isApproved ? (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg border border-cool-blue/20 bg-cool-blue/5 px-4 py-4 text-sm text-slate">
              <p>
                Serás redirigido al app en {redirectCountdown}{' '}
                {redirectCountdown === 1 ? 'segundo' : 'segundos'}.
              </p>
            </div>
            <Button onClick={handleGoToApp}>Ir al app</Button>
          </div>
        ) : null}
      </div>

      <form ref={payuFormRef} method="post" action={order?.url ?? ''}>
        <input name="tax" type="hidden" value={0} />
        <input name="taxReturnBase" type="hidden" value="0" />
        <input name="currency" type="hidden" value={order?.currency ?? 'COP'} />
        <input name="lng" type="hidden" value="es" />
        <input name="merchantId" type="hidden" value={order?.merchantId ?? ''} />
        <input name="accountId" type="hidden" value={order?.accountId ?? ''} />
        <input name="description" type="hidden" value={order?.description ?? ''} />
        <input name="referenceCode" type="hidden" value={order?.referenceCode ?? ''} />
        <input name="amount" type="hidden" value={order?.amountValue ?? '0'} />
        <input name="signature" type="hidden" value={order?.signature ?? ''} />
        <input name="buyerFullName" type="hidden" value={order?.buyerFullName ?? ''} />
        <input name="buyerEmail" type="hidden" value={order?.buyerEmail ?? ''} />
        <input name="responseUrl" type="hidden" value={`${window.location.origin}/payment`} />
        <input name="confirmationUrl" type="hidden" value={order?.confirmationUrl ?? ''} />
      </form>
    </section>
  );
}
